/**
 * AxLabs Experiments - Diffusion and Osmosis
 *
 * Simulates molecular diffusion and osmotic pressure.
 * Uses Fick's Laws and van't Hoff equation.
 *
 * Key equations:
 * - Fick's First Law: J = -D * (dC/dx)
 * - Fick's Second Law: ∂C/∂t = D * ∂²C/∂x²
 * - Osmotic pressure: π = iMRT
 */
import { BaseSimulationEngine, DIMENSIONS, } from '@axlabs/simulation-core';
// ============================================================================
// CONSTANTS
// ============================================================================
const R_GAS = 8.314; // J/(mol·K)
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'initialConcA',
        name: 'Initial Concentration A',
        symbol: '[A]_0',
        unit: 'mM',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0,
        max: 1000,
        default: 100,
        step: 10,
        description: 'Initial solute concentration in compartment A',
    },
    {
        id: 'initialConcB',
        name: 'Initial Concentration B',
        symbol: '[B]_0',
        unit: 'mM',
        dimension: DIMENSIONS.CONCENTRATION,
        min: 0,
        max: 1000,
        default: 10,
        step: 10,
        description: 'Initial solute concentration in compartment B',
    },
    {
        id: 'membraneArea',
        name: 'Membrane Area',
        symbol: 'A',
        unit: 'cm²',
        dimension: { L: 2, M: 0, T: 0, I: 0, Θ: 0, N: 0, J: 0 },
        min: 0.1,
        max: 100,
        default: 10,
        step: 1,
        description: 'Surface area of membrane',
    },
    {
        id: 'membraneThickness',
        name: 'Membrane Thickness',
        symbol: '\\Delta x',
        unit: 'μm',
        dimension: DIMENSIONS.LENGTH,
        min: 1,
        max: 100,
        default: 10,
        step: 1,
        description: 'Thickness of membrane',
    },
    {
        id: 'diffusionCoeff',
        name: 'Diffusion Coefficient',
        symbol: 'D',
        unit: 'μm²/s',
        dimension: { L: 2, M: 0, T: -1, I: 0, Θ: 0, N: 0, J: 0 },
        min: 1,
        max: 1000,
        default: 100,
        step: 10,
        description: 'Diffusion coefficient in membrane',
    },
    {
        id: 'temperature',
        name: 'Temperature',
        symbol: 'T',
        unit: 'K',
        dimension: DIMENSIONS.TEMPERATURE,
        min: 273,
        max: 373,
        default: 310,
        step: 1,
        description: 'Absolute temperature (310K = 37°C body temp)',
    },
    {
        id: 'vantHoffFactor',
        name: "van't Hoff Factor",
        symbol: 'i',
        unit: '',
        dimension: DIMENSIONS.DIMENSIONLESS,
        min: 1,
        max: 3,
        default: 1,
        step: 0.5,
        description: 'Dissociation factor (1 for non-electrolytes, 2 for NaCl)',
    },
    {
        id: 'membranePermeability',
        name: 'Water Permeability',
        symbol: 'L_p',
        unit: 'μm/(s·Pa)',
        dimension: { L: 1, M: -1, T: 2, I: 0, Θ: 0, N: 0, J: 0 },
        min: 0,
        max: 0.1,
        default: 0.01,
        step: 0.001,
        description: 'Hydraulic permeability of membrane (0 = impermeable)',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'ficks_first',
        name: "Fick's First Law",
        latex: 'J = -D \\frac{\\Delta C}{\\Delta x}',
        variables: ['flux', 'diffusionCoeff', 'concA', 'concB', 'membraneThickness'],
        description: 'Diffusion flux proportional to concentration gradient',
    },
    {
        id: 'ficks_second',
        name: "Fick's Second Law",
        latex: '\\frac{\\partial C}{\\partial t} = D \\frac{\\partial^2 C}{\\partial x^2}',
        variables: ['diffusionCoeff'],
        description: 'Rate of concentration change due to diffusion',
    },
    {
        id: 'osmotic_pressure',
        name: 'Osmotic Pressure',
        latex: '\\pi = iMRT',
        variables: ['osmoticPressure', 'vantHoffFactor', 'temperature'],
        description: "van't Hoff equation for osmotic pressure",
    },
    {
        id: 'water_flux',
        name: 'Water Flux',
        latex: 'J_w = L_p \\cdot \\Delta\\pi',
        variables: ['waterFlux', 'membranePermeability', 'osmoticPressure'],
        description: 'Osmotic water movement through membrane',
    },
    {
        id: 'equilibrium',
        name: 'Equilibrium Condition',
        latex: '[A] = [B]',
        variables: ['concA', 'concB'],
        description: 'Concentrations equalize when flux stops',
    },
    {
        id: 'mass_conservation',
        name: 'Mass Conservation',
        latex: 'n_A + n_B = \\text{constant}',
        variables: ['concA', 'concB', 'volumeA', 'volumeB'],
        description: 'Total moles of solute is conserved',
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'equilibrium_reached',
        name: 'Equilibrium Reached',
        description: 'Concentrations have equalized',
        check: (state) => {
            const A = state.variables['concA'] ?? 0;
            const B = state.variables['concB'] ?? 0;
            return Math.abs(A - B) < 0.1 && A > 0;
        },
        severity: 'warning',
        educationalMessage: 'The system has reached equilibrium. ' +
            'Net diffusion has stopped as concentrations are equal.',
    },
    {
        id: 'cell_lysis',
        name: 'Cell Lysis Risk',
        description: 'Osmotic pressure may cause cell damage',
        check: (state) => Math.abs(state.variables['osmoticPressure'] ?? 0) > 500000,
        severity: 'warning',
        educationalMessage: 'The osmotic pressure difference is very high. ' +
            'In living cells, this could cause lysis (rupture) or crenation (shrinkage).',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const DIFFUSION_CONFIG = {
    id: 'diffusion-osmosis',
    slug: 'diffusion-osmosis',
    name: 'Diffusion and Osmosis',
    domain: 'biology',
    description: 'Explore molecular transport across membranes',
    longDescription: `
Diffusion is the net movement of molecules from high to low concentration.
Osmosis is specifically the diffusion of water across a semipermeable membrane.

Key concepts:
- Concentration gradient drives diffusion
- Diffusion rate depends on: temperature, molecular size, membrane properties
- Osmotic pressure: pressure required to stop osmosis
- Isotonic: equal concentrations (no net water movement)
- Hypertonic: higher solute concentration (water moves out)
- Hypotonic: lower solute concentration (water moves in)

Biological relevance:
- Cell membrane transport
- Kidney function
- Plant turgor pressure
- IV solution formulation

This simulation models:
- Two compartments separated by a membrane
- Solute diffusion down concentration gradient
- Water movement due to osmotic pressure
  `.trim(),
    version: 1,
    conceptTags: ['diffusion', 'osmosis', 'membrane', 'concentration-gradient', 'transport'],
    difficulty: 4,
    prerequisites: ['cell-biology', 'solutions'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'concA', type: 'parameter', parameterId: 'initialConcA' },
        { variableId: 'concB', type: 'parameter', parameterId: 'initialConcB' },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 0.01,
    maxDt: 0.1,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class DiffusionSimulation extends BaseSimulationEngine {
    config = DIFFUSION_CONFIG;
    constructor() {
        super({ concA: 100, concB: 10, flux: 0, osmoticPressure: 0, volumeA: 1, volumeB: 1, waterFlux: 0 }, { type: 'euler', dt: 0.01, maxSubsteps: 10 });
    }
    getInitialState(params) {
        const concA = params['initialConcA'] ?? 100;
        const concB = params['initialConcB'] ?? 10;
        const D = params['diffusionCoeff'] ?? 100;
        const dx = params['membraneThickness'] ?? 10;
        const T = params['temperature'] ?? 310;
        const i = params['vantHoffFactor'] ?? 1;
        // Convert units: D in μm²/s, dx in μm, concentrations in mM
        const D_m2s = D * 1e-12; // μm² to m²
        const dx_m = dx * 1e-6; // μm to m
        // Flux (mol/(m²·s)) - concentrations in mM = mol/m³
        const flux = -D_m2s * ((concB - concA) / dx_m);
        // Osmotic pressure difference (Pa)
        // π = iMRT, M in mol/L = mol/m³ * 1000
        const deltaC_mol_m3 = (concA - concB); // mM = mol/m³
        const deltaC_mol_L = deltaC_mol_m3 / 1000; // mol/L
        const osmoticPressure = i * deltaC_mol_L * R_GAS * T * 1000; // Pa
        return {
            concA,
            concB,
            flux,
            osmoticPressure,
            volumeA: 1,
            volumeB: 1,
            waterFlux: 0,
        };
    }
    computeDerivatives(state, params) {
        const D = params['diffusionCoeff'] ?? 100;
        const dx = params['membraneThickness'] ?? 10;
        const A = params['membraneArea'] ?? 10;
        const Lp = params['membranePermeability'] ?? 0.01;
        const T = params['temperature'] ?? 310;
        const i = params['vantHoffFactor'] ?? 1;
        // Convert units
        const D_m2s = D * 1e-12;
        const dx_m = dx * 1e-6;
        const A_m2 = A * 1e-4; // cm² to m²
        // Current concentration difference
        const deltaC = state.concA - state.concB;
        // Diffusion flux (mol/(m²·s))
        const flux = D_m2s * deltaC / dx_m;
        // Moles transferred per second
        const dndt = flux * A_m2; // mol/s
        // Convert to change in concentration (mM)
        // Volume in L, so dmol * 1000/V = change in mM
        const dConcA = -dndt * 1000 / state.volumeA;
        const dConcB = dndt * 1000 / state.volumeB;
        // Water flux due to osmotic pressure
        const deltaC_mol_L = deltaC / 1000;
        const osmoticPressure = i * deltaC_mol_L * R_GAS * T * 1000;
        const waterFlux = Lp * osmoticPressure * A_m2; // L/s
        return {
            concA: dConcA,
            concB: dConcB,
            volumeA: -waterFlux * 1000, // mL/s
            volumeB: waterFlux * 1000,
        };
    }
    integrate(dt) {
        const params = this.parameters;
        const D = params['diffusionCoeff'] ?? 100;
        const dx = params['membraneThickness'] ?? 10;
        const A = params['membraneArea'] ?? 10;
        const Lp = params['membranePermeability'] ?? 0.01;
        const T = params['temperature'] ?? 310;
        const i = params['vantHoffFactor'] ?? 1;
        // Convert units
        const D_m2s = D * 1e-12;
        const dx_m = dx * 1e-6;
        const A_m2 = A * 1e-4;
        // Calculate flux
        const deltaC = this.state.concA - this.state.concB;
        const flux = D_m2s * deltaC / dx_m;
        const dndt = flux * A_m2;
        // Update concentrations
        // Moles transferred = dndt * dt
        const molesTrans = dndt * dt;
        // Concentration changes
        const dConcA = -molesTrans * 1000 / this.state.volumeA;
        const dConcB = molesTrans * 1000 / this.state.volumeB;
        this.state.concA = Math.max(0, this.state.concA + dConcA);
        this.state.concB = Math.max(0, this.state.concB + dConcB);
        // Water flux (osmosis)
        const deltaC_mol_L = (this.state.concA - this.state.concB) / 1000;
        this.state.osmoticPressure = i * deltaC_mol_L * R_GAS * T * 1000;
        const waterFlux = Lp * this.state.osmoticPressure * A_m2; // L/s
        // Update volumes (simplified - assume water movement)
        if (Lp > 0) {
            const dVol = waterFlux * dt; // L
            this.state.volumeA = Math.max(0.1, this.state.volumeA - dVol);
            this.state.volumeB = Math.max(0.1, this.state.volumeB + dVol);
        }
        // Update state
        this.state.flux = flux;
        this.state.waterFlux = waterFlux * 1000; // mL/s
    }
    getPositionForStabilityCheck() {
        return this.state.concA - this.state.concB;
    }
    /**
     * Check if system is in equilibrium
     */
    isAtEquilibrium() {
        return Math.abs(this.state.concA - this.state.concB) < 0.1;
    }
    /**
     * Get time to reach 90% equilibration (approximate for simple diffusion)
     */
    getEquilibrationTime() {
        const D = this.parameters['diffusionCoeff'] ?? 100;
        const dx = this.parameters['membraneThickness'] ?? 10;
        // For simple diffusion: t ~ dx² / D
        // Time for ~90% equilibration ~ 2.3 * dx² / D
        return 2.3 * dx * dx / D;
    }
}
/**
 * Factory function
 */
export function createDiffusion() {
    return new DiffusionSimulation();
}
//# sourceMappingURL=diffusion-osmosis.js.map